<template>
  <div class="nuxt-required">
    <NuxtLayout
      name="asuostaticlayout"
      :title="$page?.admissionProfile?.heroTitle"
      :image-source="$page?.admissionProfile?.heroImage"
      :is-sticky-brand-nav="false"
      hero-section-id="admission-hero"
    >
      <!-- introduction section -->
      <section
        id="admission-overview"
        class="bg-light-2 px-space-xs py-space-md py-md-space-lg py-lg-space-xxl"
      >
        <div class="container px-lg-0">
          <div class="row">
            <div class="col-12 col-lg-8">
              <!-- title -->
              <h2 class="h2-medium mb-space-sm">
                {{ $page?.admissionProfile?.introductionTitle! }}
              </h2>
              <!-- text -->
              <div
                class="fs-medium cms-content"
                v-html="$page?.admissionProfile?.introductionDescription!"
              ></div>
            </div>
          </div>
        </div>
      </section>
      <NavbarStickyAtlas
        :nav-items="navbarItems"
        :display-apply-now="false"
        border-variant="white"
        title-weight="bold"
        add-box-shadow
        underline-variant="primary"
        title="Overview"
        :display-rfi-cta="false"
        :always-show-sticky-slot="true"
      >
        <template #stuckMenu>
          <div class="d-flex justify-content-end flex-direction-row mb-space-xxxs">
            <ButtonApplyNow
              type="modal"
              event-region="main content"
              event-section="secondary navbar"
            >
            </ButtonApplyNow>
            <button
              class="btn btn-secondary fs-medium fw-bold ms-space-sm"
              @click="smoothScrollTo('asuo-hero-rfi-section')"
            >
              Request info
            </button>
          </div>
        </template>
      </NavbarStickyAtlas>
      <!-- steps -->
      <section
        v-for="(item, index) in $page?.admissionProfile?.requirementItems"
        :id="`step-${item?.number}`"
        :key="index"
        class="bg-white py-space-md"
      >
        <SectionStepApollo
          :title="item?.title!"
          :image-position="isEven(item?.number!) ? 'right' : 'left'"
          :image-source="item?.image!"
          :step-number="item?.number!"
        >
          <div class="cms-content" v-html="item?.description"></div>
        </SectionStepApollo>

        <div v-if="item?.contentTabs && item?.contentTabs.length > 0">
          <div class="container px-lg-space-xxxl">
            <div class="row">
              <div class="col-12">
                <div v-if="item.contentTabsTitle">
                  <h3 class="h3-large mb-space-sm mb-lg-space-md">
                    {{ item.contentTabsTitle }}
                  </h3>
                </div>
                <div v-for="(tab, index) in item.contentTabs" :key="index">
                  <CollapseItem
                    class="bg-white mb-space-xs mb-lg-space-sm"
                    :collapse-id="`step-${item.number}-tab-${index}`"
                    border-variant="light-2"
                    :highlight-width="8"
                    :display-number="false"
                    title-size="medium"
                    :title="tab?.name!"
                    :accordion-group-id="`rds-accordion-${item?.number}`"
                    @collapse-shown="
                      handleCollapseEvent('open', item?.contentTabsTitle!, item.title!, tab?.name!)
                    "
                    @collapse-hidden="
                      handleCollapseEvent(
                        'close',
                        item?.contentTabsTitle!,
                        item?.title!,
                        tab?.name!
                      )
                    "
                  >
                    <p v-if="tab?.title" class="fs-large fw-bold">
                      {{ tab.title }}
                    </p>
                    <div class="cms-content" v-html="tab?.text"></div>
                  </CollapseItem>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="item?.equivalencyTitle && item?.equivalencyDisclaimer">
          <degree-equivalency
            :title="item?.equivalencyTitle!"
            :equivalency-disclaimer="item?.equivalencyDisclaimer!"
          />
        </div>
      </section>

      <!-- section-experience -->
      <section
        class="section-bg rds-lazy bg-dark-3 py-space-lg py-md-space-xxl"
        data-background-image="https://cms.asuonline.asu.edu/sites/default/files/dark%20background%20pattern%402x.jpg"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-10 offset-lg-1">
              <CardInfoHorizontal
                :bg-image-source="
                  ($page?.admissionProfile?.customBlockImage || undefined) as string
                "
                :image-fit="$page?.admissionProfile?.customBlockImage ? 'cover' : 'contain'"
                bg-image-alt=""
                image-position="left"
                class="experience-card"
                border-variant="light-4"
                :display-label="false"
                :display-highlight="false"
                :title="$page?.admissionProfile?.customBlockTitle! as string"
                title-level="h2"
                title-size="small"
                :cta-as-button="true"
                cta-button-variant="secondary"
                cta-text-variant="dark-3"
                cta-text-size="medium"
                cta-text-weight="bold"
                cta-text="Learn about being a future student"
                :cta-link="$page?.admissionProfile?.customBlockLinkUrl as string"
                @cta-click="handleExperienceCtaClick($event)"
              >
                <div
                  class="mb-space-sm cms-content"
                  v-html="$page?.admissionProfile?.customBlockDescription"
                ></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>
    </NuxtLayout>
  </div>
</template>
<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CollapseItem } from "@rds-vue-ui/collapse-item";
import { NavbarStickyAtlas } from "@rds-vue-ui/navbar-sticky-atlas";
import { SectionStepApollo } from "@rds-vue-ui/section-step-apollo";
import DegreeEquivalency from "~/components/admissions/DegreeEquivalency.vue";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";
import type { AdmissionProfile } from "~/server/resolvers/resolvers-types";
import type { AdmissionProfileQuery } from "~/types/graphql-operations";
const schemaService = useSchemaService();
const route = useRoute();
const siteUrl = useRuntimeConfig().public.siteUrl;
interface IItems {
  [key: string]: string;
}

const navbarItems = computed(() => {
  const items: IItems = {};
  if ($page?.admissionProfile?.requirementItems) {
    $page?.admissionProfile?.requirementItems.forEach((item) => {
      if (item?.number) items[`step-${item?.number}`] = `Step ${item?.number}`;
    });
  }
  return items;
});
/*
********************************************
Page quries
********************************************
*/

const $page = await usePageQuery<AdmissionProfileQuery>(
  /* GraphQL */ `
    #graphql
    query AdmissionProfile($slug: String!) {
      admissionProfile(slug: $slug) {
        id
        detailPage
        contentHub {
          id
          title
          slug
        }
        title
        heroTitle
        heroImage
        introductionTitle
        introductionDescription
        customBlockTitle
        customBlockDescription
        customBlockLinkUrl
        customBlockImage
        metaTitle
        metaDescription
        requirementItems {
          number
          title
          description
          image
          contentTabsTitle
          contentTabs {
            order
            title
            text
            name
          }
          equivalencyTitle
          equivalencyDescription
          equivalencyDisclaimer
        }
      }
    }
  `,
  {
    slug: route?.params?.slug as string
  },
  { key: `admissions-apply-${route?.params?.slug as string}` }
);

/*
********************************************
Page meta tags
********************************************
*/
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});
useHead({
  title: $page?.admissionProfile?.metaTitle,
  meta: [
    {
      name: "Title",
      content: $page?.admissionProfile?.metaTitle
    },
    {
      name: "Description",
      content: $page?.admissionProfile?.metaDescription
    },
    ...useUtilityService().generateSocialTags(
      $page?.admissionProfile?.metaTitle as string,
      $page?.admissionProfile?.metaDescription as string,
      $page?.admissionProfile?.heroImage as string
    )
  ],
  link: [
    {
      rel: "canonical",
      href: useUtilityService().formatCanonicalUrl(
        `${siteUrl}${$page?.admissionProfile?.detailPage}`
      )
    }
  ],
  script: [
    schemaService.generateAdmissionProfileSchema($page?.admissionProfile as AdmissionProfile)
  ]
});
const isEven = (index: string) => {
  return Number(index) % 2 === 0;
};

const handleCollapseEvent = (
  action: string,
  tabSection: string,
  stepSection: string,
  text: string
) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action,
    "click",
    "main content",
    tabSection ? tabSection.toLowerCase() : stepSection.toLowerCase(),
    text.toLowerCase()
  );
};
const smoothScrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
    analyticsComposable.trackEvent({
      event: "link",
      name: "onclick",
      action: "click",
      type: "internal link",
      region: "navbar",
      section: "sticky navbar",
      text: "request info",
      component: "sticky navbar"
    });
  }
};
const handleExperienceCtaClick = (event: Event) => {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.admissionProfile?.customBlockTitle?.toLowerCase() as string,
    "learn about being a future student",
    "card - info horizontal"
  );
  navigateTo($page?.admissionProfile?.customBlockLinkUrl as string, {
    open: {
      target: "_self"
    }
  });
};
</script>
<style lang="scss" scoped>
.cms-content :deep(a) {
  text-decoration: underline;
}

.section-bg {
  background-size: auto;
  background-position: top;
  background-repeat: repeat;
}

section[id^="step-"] :deep(section) {
  padding-block: 0 !important;
}

.experience-card {
  max-width: 920px;
  margin-inline: auto;
}

.experience-card :deep(p:last-child) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .experience-card :deep(.bg-image.image-fit-cover) {
    min-height: 0;
  }
}

@media (min-width: 992px) {
  :deep(.description-div) {
    background-color: var(--rds-light-2, #f1f1f1) !important;
  }

  .experience-card :deep(.cih-body) {
    padding: 3rem !important;
  }
}
</style>
